import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { SlideBetMode, SlideGamePhase, SlideGameState, SlideRoundPlayer } from '@/types/slide'
import type { RootState } from '@/store/store'

export const initialSlideState: SlideGameState = {
	phase: 'idle',
	slideGameId: null,
	currencyCode: 'USD',
	betMode: 'manual',
	betAmount: '1',
	targetMultiplier: '2.00',
	autoBetEnabled: false,
	hasBet: false,
	scheduledBet: false,
	slidePoint: 0,
	stripMultipliers: [],
	winAmount: 0,
	isWinner: false,
	bettingWindowSeconds: 0,
	bettingEndsAt: null,
	playerCount: 0,
	totalBetAmount: 0,
	players: [],
	history: [],
	publicSeed: '',
	privateHash: '',
	privateSeed: '',
	isSubmitting: false,
	animationEnded: false,
}

const slideSlice = createSlice({
	name: 'slide',
	initialState: initialSlideState,
	reducers: {
		resetSlideGame(state) {
			return {
				...initialSlideState,
				currencyCode: state.currencyCode,
				betAmount: state.betAmount,
				targetMultiplier: state.targetMultiplier,
				betMode: state.betMode,
				autoBetEnabled: state.autoBetEnabled,
			}
		},
		setSlideCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setSlidePhase(state, action: PayloadAction<SlideGamePhase>) {
			state.phase = action.payload
		},
		setSlideGameId(state, action: PayloadAction<string | null>) {
			state.slideGameId = action.payload
		},
		setSlideBetMode(state, action: PayloadAction<SlideBetMode>) {
			state.betMode = action.payload
			state.autoBetEnabled = action.payload === 'auto'
		},
		setSlideBetAmount(state, action: PayloadAction<string>) {
			if (state.hasBet || state.scheduledBet) {
				return
			}

			state.betAmount = action.payload
		},
		setSlideTargetMultiplier(state, action: PayloadAction<string>) {
			if (state.hasBet || state.scheduledBet) {
				return
			}

			state.targetMultiplier = action.payload
		},
		setSlideScheduledBet(state, action: PayloadAction<boolean>) {
			state.scheduledBet = action.payload
		},
		setSlideHistory(state, action: PayloadAction<number[]>) {
			state.history = action.payload
		},
		setSlidePreviousResult(state, action: PayloadAction<{ slidePoint: number; numbers: number[] }>) {
			if (state.stripMultipliers.length > 0 || state.phase === 'playing') {
				return
			}

			state.slidePoint = action.payload.slidePoint
			state.stripMultipliers = action.payload.numbers
			state.animationEnded = true
		},
		setSlideSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setSlideAnimationEnded(state, action: PayloadAction<boolean>) {
			const ending = action.payload && !state.animationEnded
			state.animationEnded = action.payload

			if (ending && state.phase === 'result' && state.slidePoint > 0) {
				state.history = [...state.history, state.slidePoint].slice(-20)
				state.hasBet = false
			}
		},
		setSlideRoundStats(state, action: PayloadAction<{ playerCount: number; totalBetAmount: number }>) {
			state.playerCount = action.payload.playerCount
			state.totalBetAmount = action.payload.totalBetAmount
		},
		setSlideRoundPlayers(state, action: PayloadAction<SlideRoundPlayer[]>) {
			state.players = action.payload
		},
		upsertSlideRoundPlayer(state, action: PayloadAction<SlideRoundPlayer>) {
			const index = state.players.findIndex((player) => player.playerId === action.payload.playerId)
			if (index >= 0) {
				state.players[index] = action.payload
			} else {
				state.players.unshift(action.payload)
			}
		},
		applySlideRoundWaiting(
			state,
			action: PayloadAction<{
				slideGameId: string | null
				bettingWindowSeconds: number
				publicSeed?: string
				privateHash?: string
			}>,
		) {
			state.phase = 'waiting'
			state.hasBet = false
			state.winAmount = 0
			state.isWinner = false
			state.isSubmitting = false
			state.players = []
			state.playerCount = 0
			state.totalBetAmount = 0
			state.privateSeed = ''
			state.slideGameId = action.payload.slideGameId
			state.bettingWindowSeconds = action.payload.bettingWindowSeconds
			state.bettingEndsAt = action.payload.bettingWindowSeconds ? Date.now() + action.payload.bettingWindowSeconds * 1000 : null
			state.publicSeed = action.payload.publicSeed ?? ''
			state.privateHash = action.payload.privateHash ?? ''
		},
		applySlideRoundPlaying(
			state,
			action: PayloadAction<{
				slideGameId: string | null
				numbers: number[]
				publicSeed?: string
				privateHash?: string
				privateSeed?: string
			}>,
		) {
			state.phase = 'playing'
			// Official slide_point arrives on roundStopped; strip spins until then.
			state.slidePoint = 0
			state.slideGameId = action.payload.slideGameId
			state.stripMultipliers = action.payload.numbers
			state.bettingWindowSeconds = 0
			state.bettingEndsAt = null
			state.animationEnded = false
			state.winAmount = 0
			state.isWinner = false
			state.publicSeed = action.payload.publicSeed ?? state.publicSeed
			state.privateHash = action.payload.privateHash ?? state.privateHash
			state.privateSeed = action.payload.privateSeed ?? ''

			if (state.autoBetEnabled && !state.hasBet) {
				state.scheduledBet = true
			}
		},
		applySlideRoundResult(state, action: PayloadAction<{ slidePoint: number; winAmount: number; isWinner: boolean }>) {
			state.phase = 'result'
			state.slidePoint = action.payload.slidePoint
			state.winAmount = action.payload.winAmount
			state.isWinner = action.payload.isWinner
			state.bettingWindowSeconds = 0
			state.bettingEndsAt = null
			state.animationEnded = false
			// Keep hasBet until the strip finishes landing so amount/Bet Next stay locked for this round's bettors.
		},
		applySlideBetPlaced(state) {
			state.hasBet = true
			state.scheduledBet = false
			state.isSubmitting = false
		},
		applySlideBetCancelled(state) {
			state.hasBet = false
			state.scheduledBet = false
			state.isSubmitting = false
		},
	},
})

export const {
	resetSlideGame,
	setSlideCurrencyCode,
	setSlidePhase,
	setSlideGameId,
	setSlideBetMode,
	setSlideBetAmount,
	setSlideTargetMultiplier,
	setSlideScheduledBet,
	setSlideHistory,
	setSlidePreviousResult,
	setSlideSubmitting,
	setSlideAnimationEnded,
	setSlideRoundStats,
	setSlideRoundPlayers,
	upsertSlideRoundPlayer,
	applySlideRoundWaiting,
	applySlideRoundPlaying,
	applySlideRoundResult,
	applySlideBetPlaced,
	applySlideBetCancelled,
} = slideSlice.actions

export const selectSlideGame = (state: RootState) => state.slide
export const selectSlidePlayers = (state: RootState) => state.slide.players
export const selectSlideHistory = (state: RootState) => state.slide.history
export const selectSlidePhase = (state: RootState) => state.slide.phase

export default slideSlice.reducer
